'use client';

import React, { useEffect, useState } from 'react';
import { toast, Toaster } from 'sonner';
import GoogleTranslateWidget from '@/components/GoogleTranslateWidget';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { mustUsePwaOnMobile, isStandalonePwa } from '@/lib/pwaDetect';
import { isPwaInstallGateExemptPath } from '@/lib/pwaGatePaths';
import { buildPwaHandoffUrl, runSessionUrlSync } from '@/lib/pwaSession';
import { usePathname } from 'next/navigation';

type Props = {
  children: React.ReactNode;
};

export default function PwaRequiredGate({ children }: Props) {
  const pathname = usePathname();
  const [blocked, setBlocked] = useState(false);
  const [handoffUrl, setHandoffUrl] = useState('');

  const gateExempt = isPwaInstallGateExemptPath(pathname);

  useEffect(() => {
    if (gateExempt) {
      setBlocked(false);
      return;
    }
    const sync = () => setBlocked(mustUsePwaOnMobile());
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [gateExempt]);

  useEffect(() => {
    if (gateExempt || !blocked) return;
    runSessionUrlSync();
    setHandoffUrl(buildPwaHandoffUrl());
  }, [blocked, gateExempt]);

  if (gateExempt) {
    return <>{children}</>;
  }

  if (!blocked) {
    return <>{children}</>;
  }

  return (
    <>
    <GoogleTranslateWidget />
    <Toaster position="bottom-center" richColors />
    <div
      className="pwa-install-modal fixed inset-0 z-[10000] flex flex-col items-center justify-center px-6 text-center overflow-y-auto overscroll-y-contain h-dvh max-h-dvh w-full"
      style={{ backgroundColor: '#FAF8F6' }}
    >
      <img
        src="/apple-touch-icon.png"
        alt="DreamerLife"
        width={96}
        height={96}
        className="rounded-2xl mb-5 shadow-lg object-contain"
        style={{ backgroundColor: '#FFFFFF' }}
      />
      <div className="mb-5 flex flex-col items-center gap-2 w-full max-w-md relative z-[10020] overflow-visible">
        <p
          className="text-[10px] font-bold uppercase tracking-wider"
          style={{ color: '#8A8598', fontFamily: "'Acme', 'Work Sans', Helvetica, sans-serif" }}
        >
          Langue
        </p>
        <div className="flex justify-center">
          <div className="hidden sm:block">
            <LanguageSwitcher variant="desktop" />
          </div>
          <div className="sm:hidden">
            <LanguageSwitcher variant="mobile" />
          </div>
        </div>
      </div>
      <h1
        className="text-2xl font-bold mb-3 max-w-sm"
        style={{ fontFamily: "'Abel', sans-serif", color: '#1F1D40' }}
      >
        Installez DreamerLife sur votre écran d&apos;accueil
      </h1>
      <p
        className="text-sm leading-relaxed max-w-md mb-6"
        style={{ color: '#4A4560', fontFamily: "'Work Sans', sans-serif" }}
      >
        Sur téléphone et tablette, l&apos;application fonctionne uniquement en mode application installée (PWA).
        Utilisez le menu de votre navigateur puis <strong>« Ajouter à l&apos;écran d&apos;accueil »</strong>
        ou <strong>« Installer l&apos;application »</strong>.
      </p>
      <ol
        className="text-left text-sm max-w-md space-y-3 mb-8 list-decimal list-inside"
        style={{ color: '#8A8598', fontFamily: "'Work Sans', sans-serif" }}
      >
        <li>Ouvrez DreamerLife dans <strong>Safari</strong> (iPhone/iPad) ou <strong>Chrome</strong> (Android)</li>
        <li>
          <strong>iPhone / iPad :</strong> bouton <strong>Partager</strong> → <strong>Sur l&apos;écran d&apos;accueil</strong>
        </li>
        <li>
          <strong>Android (téléphone ou tablette) :</strong> menu <strong>⋮</strong> en haut à droite →{' '}
          <strong>Installer l&apos;application</strong> ou <strong>Ajouter à l&apos;écran d&apos;accueil</strong>
        </li>
        <li>Relancez l&apos;icône DreamerLife depuis votre écran d&apos;accueil</li>
        {handoffUrl.includes('token=') && (
          <li>
            Si vous n&apos;êtes pas connecté dans l&apos;app installée, ouvrez une fois le{' '}
            <strong>lien de session</strong> ci-dessous (depuis Safari ou en le collant dans la barre d&apos;adresse de l&apos;app)
          </li>
        )}
      </ol>
      {handoffUrl.includes('token=') && (
        <div className="w-full max-w-md mb-6 text-left">
          <p
            className="text-xs mb-2"
            style={{ color: '#8A8598', fontFamily: "'Work Sans', sans-serif" }}
          >
            Lien de session (conserve votre compte dans la PWA) :
          </p>
          <p
            className="text-xs break-all rounded-xl px-3 py-2 mb-3"
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E8E4DF',
              color: '#4A4560',
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            {handoffUrl}
          </p>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(handoffUrl);
                toast.success('Lien copié');
              } catch {
                toast.error('Copie impossible — maintenez le lien pour le sélectionner');
              }
            }}
            className="w-full py-3 text-sm font-semibold rounded-full transition-opacity hover:opacity-95"
            style={{
              backgroundColor: '#D41F4C',
              color: '#FFFFFF',
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            Copier le lien de session
          </button>
        </div>
      )}
      {!isStandalonePwa() && (
        <p className="text-xs" style={{ color: '#D41F4C', fontFamily: "'Work Sans', sans-serif" }}>
          Cette page reste bloquée tant que l&apos;app n&apos;est pas ouverte depuis l&apos;icône installée.
        </p>
      )}
    </div>
    </>
  );
}
